import * as React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "next-themes";

import { AuthProvider } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import OrgView from "@/pages/OrgView";
import Sites from "@/pages/Sites";
import HR from "@/pages/HR";
import Rotas from "@/pages/Rotas";
import Comms from "@/pages/Comms";
import BoardsIndex from "@/pages/BoardsIndex";
import BoardView from "@/pages/BoardView";
import Pay from "@/pages/Pay";
import PayV2 from "@/pages/PayV2";
import Bookings from "@/pages/Bookings";
import Plus from "@/pages/Plus";
import Books from "@/pages/Books";
import Alerts from "@/pages/Alerts";
import Roles from "@/pages/Roles";
import Rules from "@/pages/Rules";
import Wall from "@/pages/Wall";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import Cases from "@/pages/Cases";
import Queue from "@/pages/Queue";
import ModuleStub from "@/pages/ModuleStub";

// Marketing tree — lazy so the authed app doesn't pay its bundle cost.
const PublicShell = React.lazy(() => import("@/pages/marketing/PublicShell"));
const LandingV2 = React.lazy(() => import("@/pages/marketing/LandingV2"));
const ProductPage = React.lazy(() => import("@/pages/marketing/ProductPage"));
const SolutionPage = React.lazy(() => import("@/pages/marketing/SolutionPage"));
const ResourcePage = React.lazy(() => import("@/pages/marketing/ResourcePage"));
const Pricing = React.lazy(() => import("@/pages/marketing/Pricing"));
const AppsPage = React.lazy(() => import("@/pages/marketing/AppsPage"));
const GetApp = React.lazy(() => import("@/pages/marketing/GetApp"));

// Public embeddable widget — no marketing chrome, no auth.
const BookingWidget = React.lazy(() => import("@/pages/BookingWidget"));

// Phase 11 — printable invoice (authed, plain iframe).
const InvoicePrint = React.lazy(() => import("@/pages/InvoicePrint"));

// Phase 12.2 — public anonymous whistleblowing report page (no auth).
const PublicReport = React.lazy(() => import("@/pages/PublicReport"));

function MarketingLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#FBF8F2" }}
      data-testid="mkt-loading"
    >
      <div className="h-8 w-8 rounded-full border-2 border-black/10 border-t-[#6E3CFA] animate-spin" />
    </div>
  );
}

function MarketingRoute({ element }) {
  return (
    <React.Suspense fallback={<MarketingLoader />}>
      <PublicShell>{element}</PublicShell>
    </React.Suspense>
  );
}

function App() {
  // Attach data-density on <html> so every page picks up compact mode.
  // Gated on pointer:fine at the CSS layer — never dropping below 44px
  // on touch devices per binding constraint.
  React.useEffect(() => {
    try {
      const d = localStorage.getItem("fable.density") || "comfortable";
      document.documentElement.setAttribute("data-density", d);
    } catch {}
  }, []);
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      storageKey="fable.theme"
    >
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public marketing site */}
          <Route path="/" element={<MarketingRoute element={<LandingV2 />} />} />
          <Route path="/pricing" element={<MarketingRoute element={<Pricing />} />} />
          <Route path="/products/:slug" element={<MarketingRoute element={<ProductPage />} />} />
          <Route path="/solutions/:slug" element={<MarketingRoute element={<SolutionPage />} />} />
          <Route path="/resources/:slug" element={<MarketingRoute element={<ResourcePage />} />} />
          <Route path="/apps" element={<MarketingRoute element={<AppsPage />} />} />
          <Route path="/apps/:slug" element={<MarketingRoute element={<AppsPage />} />} />
          <Route path="/get/:slug" element={<MarketingRoute element={<GetApp />} />} />

          {/* Public no-auth embeddable widget — routes outside PublicShell so
              it renders naked inside iframes. */}
          <Route path="/widget/:widgetId" element={<React.Suspense fallback={null}><BookingWidget /></React.Suspense>} />

          {/* Public anonymous whistleblowing report — no auth, no shell. */}
          <Route
            path="/report/:token"
            element={<React.Suspense fallback={<MarketingLoader />}><PublicReport /></React.Suspense>}
          />

          {/* Printable invoice — authed but renders plain (no shell). */}
          <Route
            path="/c/:companyId/invoices/:invoiceId/print"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={null}>
                  <InvoicePrint />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Authed */}
          <Route
            path="/app"
            element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
          />
          <Route
            path="/c/:companyId"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <AppShell />
                </CompanyProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="wall" element={<Wall />} />
            <Route path="rules" element={<Rules />} />
            <Route path="members" element={<Members />} />
            <Route path="org" element={<OrgView />} />
            <Route path="sites" element={<Sites />} />
            <Route path="roles" element={<Roles />} />
            <Route path="settings" element={<Settings />} />
            <Route path="audit" element={<Audit />} />
            <Route path="m/hr" element={<HR />} />
            <Route path="m/rotas" element={<Rotas />} />
            <Route path="m/comms" element={<Comms />} />
            <Route path="m/boards" element={<BoardsIndex />} />
            <Route path="m/boards/:boardId" element={<BoardView />} />
            <Route path="m/pay" element={<Pay />} />
            <Route path="m/pay/v2" element={<PayV2 />} />
            <Route path="m/pay/v2/:periodId" element={<PayV2 />} />
            <Route path="m/bookings" element={<Bookings />} />
            <Route path="m/plus" element={<Plus />} />
            <Route path="m/books" element={<Books />} />
            <Route path="m/cases" element={<Cases />} />
            <Route path="queue" element={<Queue />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="m/:moduleId" element={<ModuleStub />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      <Toaster richColors position="bottom-right" />
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
